import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UserPlus, Mail, Phone, User, Building2, MapPin, IdCard, CreditCard, Cake, Users2, StickyNote } from "lucide-react";
import { fetchExternals, createExternal } from "../lib/driveClient";
import ContactDetailModal from "../components/ContactDetailModal";
import { EntityFormModal, EntityField } from "../components/EntityFormModal";

const empty = {
  first_name: "",
  last_name: "",
  full_name: "",
  company: "",
  relation: "",
  email: "",
  phone: "",
  address: "",
  zip_city: "",
  birthdate: "",
  tax_id: "",
  iban: "",
  bic: "",
  bank_name: "",
  notes: "",
};

export default function DriveExternals({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [openContact, setOpenContact] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setRows(await fetchExternals()); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.full_name) {
      payload.full_name = [payload.first_name, payload.last_name].filter(Boolean).join(" ").trim();
    }
    if (!payload.full_name) { toast.error("Anzeigename ist Pflicht"); return; }
    setBusy(true);
    try {
      await createExternal({ ...payload, birthdate: payload.birthdate || null });
      toast.success("Externer Kontakt angelegt");
      setForm(empty);
      setShowForm(false);
      await load();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex items-start justify-between flex-wrap gap-4">
        <div>
          {!embedded && (
            <>
              <div className="flex items-center gap-3 mb-3 text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold">
                <span className="block w-8 h-px bg-[#ff0033] shadow-[0_0_8px_#ff0033]" />
                Externe
              </div>
              <h1 className="font-display font-black uppercase tracking-tighter leading-none text-3xl md:text-4xl">
                Externe Kontakte
              </h1>
            </>
          )}
          {embedded && (
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
              {rows.length} Externe{rows.length === 1 ? "r" : ""}
            </div>
          )}
        </div>
        <button
          onClick={() => setShowForm(true)}
          data-testid="btn-new-external"
          className="inline-flex items-center gap-2 px-5 py-3 text-[10px] font-bold tracking-[0.25em] uppercase bg-white text-black hover:bg-[#ff0033] hover:text-white transition-colors"
        >
          <UserPlus size={13} /> Neuer Externer
        </button>
      </motion.div>

      {loading ? (
        <div className="p-10 text-center text-sm text-zinc-500">Lade…</div>
      ) : rows.length === 0 ? (
        <div className="bg-[#0A0A0A] border border-white/[0.06] p-10 text-center text-sm text-zinc-500">
          Noch keine Externen. Lege den ersten oben an — z.B. für NDAs mit Familie, Freunden oder Beratern.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((x) => (
            <div
              key={x.id}
              data-testid={`external-${x.id}`}
              onClick={() => window.location.assign(`/drive/externals/${x.id}`)}
              className="bg-[#0A0A0A] border border-white/[0.06] p-5 hover:border-[#ff0033]/30 transition-colors cursor-pointer group"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-2 flex items-center justify-between">
                <span>Extern{x.relation ? ` · ${x.relation}` : ""}</span>
                <span className="text-zinc-600 group-hover:text-zinc-400 tracking-normal">Öffnen →</span>
              </div>
              <div className="font-display font-black uppercase text-xl tracking-tight mb-1 truncate">{x.full_name}</div>
              <div className="mt-4 space-y-1.5 text-xs text-zinc-400">
                {x.email && (
                  <div className="flex items-center gap-2 truncate">
                    <Mail size={11} className="text-zinc-600 shrink-0" /> {x.email}
                  </div>
                )}
                {x.phone && (
                  <div className="flex items-center gap-2 truncate">
                    <Phone size={11} className="text-zinc-600 shrink-0" /> {x.phone}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <EntityFormModal
          title="Neuen Externen anlegen"
          onClose={() => setShowForm(false)}
          onSubmit={submit}
          busy={busy}
          submitLabel="Externen anlegen"
        >
          <EntityField icon={<User size={13} />} label="Anzeigename" required full value={form.full_name} onChange={upd("full_name")} placeholder="z.B. Marino Rogait" testid="ext-fullname" />
          <EntityField icon={<Users2 size={13} />} label="Beziehung / Rolle" value={form.relation} onChange={upd("relation")} placeholder="z.B. Bruder, Anwalt" testid="ext-relation" />
          <EntityField icon={<Building2 size={13} />} label="Firma / Institution" value={form.company} onChange={upd("company")} testid="ext-company" />
          <EntityField icon={<User size={13} />} label="Vorname" value={form.first_name} onChange={upd("first_name")} testid="ext-firstname" />
          <EntityField icon={<User size={13} />} label="Nachname" value={form.last_name} onChange={upd("last_name")} testid="ext-lastname" />
          <EntityField icon={<Cake size={13} />} label="Geburtsdatum" type="date" value={form.birthdate} onChange={upd("birthdate")} testid="ext-birthdate" />
          <EntityField icon={<Mail size={13} />} label="E-Mail" type="email" value={form.email} onChange={upd("email")} testid="ext-email" />
          <EntityField icon={<Phone size={13} />} label="Telefon" value={form.phone} onChange={upd("phone")} testid="ext-phone" />
          <EntityField icon={<MapPin size={13} />} label="Adresse" value={form.address} onChange={upd("address")} placeholder="Straße" testid="ext-address" />
          <EntityField icon={<MapPin size={13} />} label="PLZ / Ort" value={form.zip_city} onChange={upd("zip_city")} testid="ext-zipcity" />
          <EntityField icon={<IdCard size={13} />} label="Steuer-ID" value={form.tax_id} onChange={upd("tax_id")} testid="ext-taxid" />
          <EntityField icon={<CreditCard size={13} />} label="IBAN" value={form.iban} onChange={upd("iban")} testid="ext-iban" />
          <EntityField icon={<CreditCard size={13} />} label="BIC" value={form.bic} onChange={upd("bic")} testid="ext-bic" />
          <EntityField icon={<CreditCard size={13} />} label="Bank" full value={form.bank_name} onChange={upd("bank_name")} testid="ext-bank" />
          <EntityField icon={<StickyNote size={13} />} label="Notizen" full rows={3} value={form.notes} onChange={upd("notes")} testid="ext-notes" />
        </EntityFormModal>
      )}

      {openContact && (
        <ContactDetailModal
          contact={openContact}
          type="external"
          onClose={() => setOpenContact(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
